import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'
import { ArrowLeft, Mail, Phone, Building, Briefcase, Calendar, Tag, Edit, MessageSquare, TrendingUp, Save, X, Plus, User, Globe, Linkedin, Twitter, MapPin, Clock, FileText, Paperclip, RefreshCw, ExternalLink, Download, Cloud } from 'lucide-react'
import api from '../lib/api'
import { format } from 'date-fns'

const getStatusColor = (status) => {
  const colors = {
    'New': 'bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300',
    'Contacted': 'bg-yellow-100 dark:bg-yellow-900/50 text-yellow-700 dark:text-yellow-300',
    'Qualified': 'bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-300',
    'Proposal Sent': 'bg-purple-100 dark:bg-purple-900/50 text-purple-700 dark:text-purple-300',
    'Negotiation': 'bg-orange-100 dark:bg-orange-900/50 text-orange-700 dark:text-orange-300',
    'Won': 'bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-300',
    'Lost': 'bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-300',
    'Nurturing': 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300',
    'Attempted to Contact': 'bg-amber-100 dark:bg-amber-900/50 text-amber-700 dark:text-amber-300',
    'Not Contacted': 'bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300',
    'Contact in Future': 'bg-cyan-100 dark:bg-cyan-900/50 text-cyan-700 dark:text-cyan-300',
    'Junk Lead': 'bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-300',
    'Lost Lead': 'bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-300',
    'Converted': 'bg-emerald-100 dark:bg-emerald-900/50 text-emerald-700 dark:text-emerald-300',
    'Responded': 'bg-teal-100 dark:bg-teal-900/50 text-teal-700 dark:text-teal-300',
    'Pre-Qualified': 'bg-indigo-100 dark:bg-indigo-900/50 text-indigo-700 dark:text-indigo-300',
    'Assigned': 'bg-purple-100 dark:bg-purple-900/50 text-purple-700 dark:text-purple-300'
  }
  return colors[status] || 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
}

const LeadDetails = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const [lead, setLead] = useState(null)
  const [loading, setLoading] = useState(true)
  const [editingScore, setEditingScore] = useState(false)
  const [newScore, setNewScore] = useState(0)
  const [scoreReason, setScoreReason] = useState('')
  const [editingStatus, setEditingStatus] = useState(false)
  const [newStatus, setNewStatus] = useState('')
  const [newComment, setNewComment] = useState('')
  const [newTag, setNewTag] = useState('')
  const [showTagInput, setShowTagInput] = useState(false)
  const [activeTab, setActiveTab] = useState('overview')
  const [zohoDetails, setZohoDetails] = useState(null)
  const [loadingZoho, setLoadingZoho] = useState(false)

  useEffect(() => {
    fetchLeadDetails()
  }, [id])

  // Fetch Zoho details if lead has crmId
  const fetchZohoDetails = async (crmId) => {
    if (!crmId) return
    setLoadingZoho(true)
    try {
      const response = await api.get(`/crm/zoho/leads/${crmId}/details`)
      setZohoDetails(response.data)
    } catch (error) {
      console.error('Failed to fetch Zoho details:', error)
    } finally {
      setLoadingZoho(false)
    }
  }

  const fetchLeadDetails = async () => {
    try {
      const response = await api.get(`/crm/leads/${id}`)
      setLead(response.data)
      setNewScore(response.data.score || 0)
      setNewStatus(response.data.status)
      // Fetch Zoho details if lead has crmId
      if (response.data.crmId) {
        fetchZohoDetails(response.data.crmId)
      }
    } catch (error) {
      console.error('Failed to fetch lead:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleAddComment = async () => {
    if (!newComment.trim()) return
    
    try {
      // Save locally first
      await api.post(`/crm/leads/${id}/comments`, { text: newComment })
      
      // Sync to Zoho if lead has crmId
      if (lead.crmId) {
        try {
          const zohoResult = await api.post(`/crm/zoho/leads/${lead.crmId}/notes`, { 
            text: newComment,
            title: 'Note from BD Platform'
          })
          if (zohoResult.data.success) {
            toast.success('Note added and synced to Zoho CRM')
          } else {
            toast.success('Note added locally (Zoho sync failed)')
          }
        } catch (zohoErr) {
          console.error('Zoho sync error:', zohoErr)
          toast.success('Note added locally')
        }
      } else {
        toast.success('Note added')
      }
      
      setNewComment('')
      fetchLeadDetails()
      // Refresh Zoho details to show the new note
      if (lead.crmId) {
        fetchZohoDetails(lead.crmId)
      }
    } catch (error) {
      toast.error('Failed to add note')
    }
  }

  const handleUpdateScore = async () => {
    try {
      await api.put(`/crm/leads/${id}/score`, { score: newScore, reason: scoreReason })
      toast.success('Score updated')
      setEditingScore(false)
      setScoreReason('')
      fetchLeadDetails()
    } catch (error) {
      toast.error('Failed to update score')
    }
  }

  const handleUpdateStatus = async () => {
    try {
      await api.put(`/crm/leads/${id}/status`, { status: newStatus })
      toast.success('Status updated')
      setEditingStatus(false)
      fetchLeadDetails()
    } catch (error) {
      toast.error('Failed to update status')
    }
  }

  const handleAddTag = async () => {
    if (!newTag.trim()) return
    
    try {
      const updatedTags = [...(lead.tags || []), newTag.trim()]
      await api.put(`/crm/leads/${id}`, { tags: updatedTags })
      toast.success('Tag added')
      setNewTag('')
      setShowTagInput(false)
      fetchLeadDetails()
    } catch (error) {
      toast.error('Failed to add tag')
    }
  }

  const handleRemoveTag = async (tagToRemove) => {
    try {
      const updatedTags = lead.tags.filter(tag => tag !== tagToRemove)
      await api.put(`/crm/leads/${id}`, { tags: updatedTags })
      toast.success('Tag removed')
      fetchLeadDetails()
    } catch (error) {
      toast.error('Failed to remove tag')
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner"></div>
      </div>
    )
  }

  if (!lead) {
    return <div className="text-center py-12">Lead not found</div>
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate('/leads')}
          className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-primary-100 dark:bg-primary-900 rounded-full flex items-center justify-center">
              <span className="text-xl font-bold text-primary-600 dark:text-primary-400">
                {lead.name?.charAt(0)?.toUpperCase() || 'L'}
              </span>
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">{lead.name}</h1>
              <p className="text-gray-600 dark:text-gray-400">{lead.company || 'No company'}</p>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {lead.crmId && (
            <button
              onClick={() => fetchZohoDetails(lead.crmId)}
              disabled={loadingZoho}
              className="flex items-center gap-2 px-3 py-2 text-sm bg-green-50 text-green-700 rounded-lg hover:bg-green-100 dark:bg-green-900/20 dark:text-green-400"
            >
              <RefreshCw className={`w-4 h-4 ${loadingZoho ? 'animate-spin' : ''}`} />
              Sync from Zoho
            </button>
          )}
          {editingStatus ? (
            <div className="flex items-center gap-2">
              <select
                value={newStatus}
                onChange={(e) => setNewStatus(e.target.value)}
                className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700"
              >
                <option value="New">New</option>
                <option value="Contacted">Contacted</option>
                <option value="Qualified">Qualified</option>
                <option value="Proposal Sent">Proposal Sent</option>
                <option value="Negotiation">Negotiation</option>
                <option value="Won">Won</option>
                <option value="Lost">Lost</option>
                <option value="Nurturing">Nurturing</option>
                <option value="Assigned">Assigned</option>
              </select>
              <button onClick={handleUpdateStatus} className="p-2 bg-green-600 text-white rounded-lg hover:bg-green-700">
                <Save className="w-4 h-4" />
              </button>
              <button onClick={() => setEditingStatus(false)} className="p-2 bg-gray-200 dark:bg-gray-600 rounded-lg hover:bg-gray-300">
                <X className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <button
              onClick={() => setEditingStatus(true)}
              className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium cursor-pointer ${
                lead.status === 'Won' || lead.status === 'Converted' ? 'bg-green-100 text-green-700' :
                lead.status === 'Qualified' ? 'bg-blue-100 text-blue-700' :
                lead.status === 'Lost' || lead.status === 'Lost Lead' ? 'bg-red-100 text-red-700' :
                lead.status === 'Assigned' ? 'bg-purple-100 text-purple-700' :
                'bg-yellow-100 text-yellow-700'
              }`}>
              {lead.status}
              <Edit className="w-3 h-3" />
            </button>
          )}
        </div>
      </div>

      {/* Tabs - Like Zoho */}
      <div className="border-b border-gray-200 dark:border-gray-700">
        <nav className="flex space-x-8">
          {['overview', 'timeline', 'notes'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`py-3 px-1 border-b-2 font-medium text-sm capitalize ${
                activeTab === tab
                  ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab}
            </button>
          ))}
        </nav>
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Lead Information */}
          <div className="lg:col-span-2 space-y-6">
            {/* Lead Information Card */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
              <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-4">Lead Information</h2>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-primary-600 dark:text-primary-400 mb-1">Company</p>
                  <p className="font-medium text-gray-900 dark:text-gray-100">{lead.company || '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-primary-600 dark:text-primary-400 mb-1">Lead Owner</p>
                  <p className="font-medium text-gray-900 dark:text-gray-100">{lead.leadOwner?.name || '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-primary-600 dark:text-primary-400 mb-1">Department</p>
                  <p className="font-medium text-gray-900 dark:text-gray-100">{lead.department || '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-primary-600 dark:text-primary-400 mb-1">Email</p>
                  <a href={`mailto:${lead.email}`} className="font-medium text-primary-600 hover:underline">{lead.email || '-'}</a>
                </div>
                <div>
                  <p className="text-xs text-primary-600 dark:text-primary-400 mb-1">Lead Name</p>
                  <p className="font-medium text-gray-900 dark:text-gray-100">{lead.name}</p>
                </div>
                <div>
                  <p className="text-xs text-primary-600 dark:text-primary-400 mb-1">Phone</p>
                  <a href={`tel:${lead.phone}`} className="font-medium text-primary-600 hover:underline">{lead.phone || '-'}</a>
                </div>
                <div>
                  <p className="text-xs text-primary-600 dark:text-primary-400 mb-1">Title</p>
                  <p className="font-medium text-gray-900 dark:text-gray-100">{lead.title || lead.designation || '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-primary-600 dark:text-primary-400 mb-1">Mobile</p>
                  <a href={`tel:${lead.mobile}`} className="font-medium text-primary-600 hover:underline">{lead.mobile || '-'}</a>
                </div>
                <div>
                  <p className="text-xs text-primary-600 dark:text-primary-400 mb-1">Lead Status</p>
                  <span className={`inline-block px-2 py-1 rounded text-xs font-medium ${getStatusColor(lead.status)}`}>{lead.status}</span>
                </div>
                <div>
                  <p className="text-xs text-primary-600 dark:text-primary-400 mb-1">LinkedIn</p>
                  {lead.linkedin ? (
                    <a href={lead.linkedin} target="_blank" rel="noopener noreferrer" className="font-medium text-primary-600 hover:underline flex items-center gap-1">
                      <Linkedin className="w-4 h-4" /> Profile
                    </a>
                  ) : <p className="text-gray-500">-</p>}
                </div>
                <div>
                  <p className="text-xs text-primary-600 dark:text-primary-400 mb-1">Lead Category</p>
                  <p className="font-medium text-gray-900 dark:text-gray-100">{lead.leadCategory || '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-primary-600 dark:text-primary-400 mb-1">Twitter</p>
                  {lead.twitter ? (
                    <a href={lead.twitter} target="_blank" rel="noopener noreferrer" className="font-medium text-primary-600 hover:underline flex items-center gap-1">
                      <Twitter className="w-4 h-4" /> Profile
                    </a>
                  ) : <p className="text-gray-500">-</p>}
                </div>
                <div>
                  <p className="text-xs text-primary-600 dark:text-primary-400 mb-1">Lead Source</p>
                  <p className="font-medium text-gray-900 dark:text-gray-100">{lead.source || '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-primary-600 dark:text-primary-400 mb-1">Industry</p>
                  <p className="font-medium text-gray-900 dark:text-gray-100">{lead.industry || '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-primary-600 dark:text-primary-400 mb-1">Rating</p>
                  <p className="font-medium text-gray-900 dark:text-gray-100">{lead.rating || '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-primary-600 dark:text-primary-400 mb-1">Website</p>
                  {lead.website ? (
                    <a href={lead.website} target="_blank" rel="noopener noreferrer" className="font-medium text-primary-600 hover:underline flex items-center gap-1">
                      <Globe className="w-4 h-4" /> {lead.website}
                    </a>
                  ) : <p className="text-gray-500">-</p>}
                </div>
              </div>
            </div>

            {/* Address Information */}
            {(lead.address?.street || lead.address?.city || lead.address?.country) && (
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-4 flex items-center gap-2">
                  <MapPin className="w-5 h-5" /> Address Information
                </h2>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-primary-600 mb-1">Street</p>
                    <p className="font-medium">{lead.address?.street || '-'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-primary-600 mb-1">City</p>
                    <p className="font-medium">{lead.address?.city || '-'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-primary-600 mb-1">State</p>
                    <p className="font-medium">{lead.address?.state || '-'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-primary-600 mb-1">Zip Code</p>
                    <p className="font-medium">{lead.address?.zipCode || '-'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-primary-600 mb-1">Country</p>
                    <p className="font-medium">{lead.address?.country || '-'}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Description */}
            {lead.description && (
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-4">Description</h2>
                <p className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{lead.description}</p>
              </div>
            )}
          </div>

          {/* Right Column - Tags & CRM Info */}
          <div className="space-y-4">
            {/* CRM Sync Info */}
            {lead.crmId && (
              <div className="bg-green-50 dark:bg-green-900/20 rounded-xl border border-green-200 dark:border-green-800 p-4">
                <h3 className="text-sm font-medium text-green-700 dark:text-green-400 mb-2 flex items-center gap-2">
                  <Cloud className="w-4 h-4" /> Zoho CRM
                </h3>
                <p className="text-xs text-green-600 dark:text-green-400">ID: {lead.crmId}</p>
                {lead.crmLastSync && (
                  <p className="text-xs text-green-600 dark:text-green-400 mb-2">Last sync: {format(new Date(lead.crmLastSync), 'MMM dd, yyyy HH:mm')}</p>
                )}
                <a 
                  href={`https://crm.zoho.in/crm/skyserve/tab/Leads/${lead.crmId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-xs text-green-700 dark:text-green-300 hover:text-green-800 dark:hover:text-green-200 font-medium mt-2"
                >
                  <ExternalLink className="w-3 h-3" /> Open in Zoho CRM
                </a>
              </div>
            )}

            {/* Tags */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400 flex items-center gap-2">
                  <Tag className="w-4 h-4" /> Tags
                </h3>
                <button onClick={() => setShowTagInput(!showTagInput)} className="text-primary-600 text-sm hover:text-primary-700">+ Add</button>
              </div>
              {showTagInput && (
                <div className="flex gap-2 mb-3">
                  <input
                    type="text"
                    value={newTag}
                    onChange={(e) => setNewTag(e.target.value)}
                    placeholder="Tag name"
                    className="flex-1 px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    onKeyPress={(e) => e.key === 'Enter' && handleAddTag()}
                  />
                  <button onClick={handleAddTag} className="px-2 py-1 bg-primary-600 text-white text-sm rounded hover:bg-primary-700">Add</button>
                </div>
              )}
              <div className="flex flex-wrap gap-2">
                {lead.tags?.length > 0 ? lead.tags.map((tag, i) => (
                  <span key={i} className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300 rounded text-xs">
                    {tag}
                    <button onClick={() => handleRemoveTag(tag)} className="hover:text-blue-900 dark:hover:text-blue-100"><X className="w-3 h-3" /></button>
                  </span>
                )) : <p className="text-sm text-gray-500 dark:text-gray-400">No tags</p>}
              </div>
            </div>
          </div>

          {/* Zoho Related Data - Full Width Below */}
          {lead.crmId && zohoDetails && (
            <div className="lg:col-span-3 space-y-6">
              {/* Open Activities */}
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100">Open Activities</h2>
                </div>
                {zohoDetails.openActivities?.length > 0 ? (
                  <div className="space-y-2">
                    {zohoDetails.openActivities.map((act, i) => (
                      <div key={i} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                        <div>
                          <p className="font-medium">{act.subject || act.type}</p>
                          <p className="text-xs text-gray-500">{act.type} • {act.status}</p>
                        </div>
                        {act.dueDate && <span className="text-xs text-gray-500">{format(new Date(act.dueDate), 'MMM dd, yyyy')}</span>}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-sm">No records found</p>
                )}
              </div>

              {/* Closed Activities */}
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-4">Closed Activities</h2>
                {zohoDetails.closedActivities?.length > 0 ? (
                  <div className="space-y-2">
                    {zohoDetails.closedActivities.map((act, i) => (
                      <div key={i} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                        <div>
                          <p className="font-medium">{act.subject || act.type}</p>
                          <p className="text-xs text-gray-500">{act.type} • {act.status}</p>
                        </div>
                        {act.dueDate && <span className="text-xs text-gray-500">{format(new Date(act.dueDate), 'MMM dd, yyyy')}</span>}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-sm">No records found</p>
                )}
              </div>

              {/* Emails */}
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-4">Emails</h2>
                {zohoDetails.emails?.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b dark:border-gray-700">
                          <th className="text-left py-2 px-3 font-medium text-gray-600">Subject</th>
                          <th className="text-left py-2 px-3 font-medium text-gray-600">Date</th>
                          <th className="text-left py-2 px-3 font-medium text-gray-600">Source</th>
                          <th className="text-left py-2 px-3 font-medium text-gray-600">Sent By</th>
                          <th className="text-left py-2 px-3 font-medium text-gray-600">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {zohoDetails.emails.map((email, i) => (
                          <tr key={i} className="border-b dark:border-gray-700">
                            <td className="py-2 px-3">
                              <div className="flex items-center gap-2">
                                <Mail className="w-4 h-4 text-gray-400" />
                                <span className="text-primary-600">{email.subject}</span>
                              </div>
                            </td>
                            <td className="py-2 px-3 text-gray-600">{email.date ? format(new Date(email.date), 'MMM dd, yyyy h:mm a') : '-'}</td>
                            <td className="py-2 px-3 text-gray-600">{email.source || '-'}</td>
                            <td className="py-2 px-3 text-gray-600">{email.sentBy || '-'}</td>
                            <td className="py-2 px-3">
                              <span className={`px-2 py-1 rounded text-xs ${email.status === 'Delivered' ? 'bg-green-100 text-green-700' : email.status === 'Opened' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700'}`}>
                                {email.status || '-'}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-gray-500 text-sm">No emails found</p>
                )}
              </div>

              {/* Campaigns */}
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-4">Campaigns</h2>
                {zohoDetails.campaigns?.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b dark:border-gray-700">
                          <th className="text-left py-2 px-3 font-medium text-gray-600">Campaign Name</th>
                          <th className="text-left py-2 px-3 font-medium text-gray-600">Status</th>
                          <th className="text-left py-2 px-3 font-medium text-gray-600">Type</th>
                          <th className="text-left py-2 px-3 font-medium text-gray-600">Start Date</th>
                          <th className="text-left py-2 px-3 font-medium text-gray-600">End Date</th>
                          <th className="text-left py-2 px-3 font-medium text-gray-600">Member Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {zohoDetails.campaigns.map((camp, i) => (
                          <tr key={i} className="border-b dark:border-gray-700">
                            <td className="py-2 px-3 text-primary-600">{camp.name}</td>
                            <td className="py-2 px-3">
                              <span className={`px-2 py-1 rounded text-xs ${camp.status === 'Active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}`}>
                                {camp.status || '-'}
                              </span>
                            </td>
                            <td className="py-2 px-3 text-gray-600">{camp.type || '-'}</td>
                            <td className="py-2 px-3 text-gray-600">{camp.startDate ? format(new Date(camp.startDate), 'MMM dd, yyyy') : '-'}</td>
                            <td className="py-2 px-3 text-gray-600">{camp.endDate ? format(new Date(camp.endDate), 'MMM dd, yyyy') : '-'}</td>
                            <td className="py-2 px-3 text-gray-600">{camp.memberStatus || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-gray-500 text-sm">No campaigns found</p>
                )}
              </div>

              {/* Attachments */}
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-4">Attachments</h2>
                {zohoDetails.attachments?.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b dark:border-gray-700">
                          <th className="text-left py-2 px-3 font-medium text-gray-600">File Name</th>
                          <th className="text-left py-2 px-3 font-medium text-gray-600">Attached By</th>
                          <th className="text-left py-2 px-3 font-medium text-gray-600">Date Added</th>
                          <th className="text-left py-2 px-3 font-medium text-gray-600">Size</th>
                          <th className="text-left py-2 px-3 font-medium text-gray-600">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {zohoDetails.attachments.map((att, i) => (
                          <tr key={i} className="border-b dark:border-gray-700">
                            <td className="py-2 px-3">
                              <div className="flex items-center gap-2">
                                <Paperclip className="w-4 h-4 text-gray-400" />
                                <span className="text-primary-600">{att.fileName}</span>
                              </div>
                            </td>
                            <td className="py-2 px-3 text-gray-600">{att.createdBy || '-'}</td>
                            <td className="py-2 px-3 text-gray-600">{att.createdTime ? format(new Date(att.createdTime), 'MMM dd, yyyy h:mm a') : '-'}</td>
                            <td className="py-2 px-3 text-gray-600">{att.size ? `${(att.size / 1024).toFixed(2)} KB` : '-'}</td>
                            <td className="py-2 px-3">
                              <button 
                                onClick={async () => {
                                  try {
                                    const response = await api.get(`/crm/zoho/leads/${lead.crmId}/attachments/${att.id}/download`, {
                                      responseType: 'blob'
                                    })
                                    const url = window.URL.createObjectURL(new Blob([response.data]))
                                    const link = document.createElement('a')
                                    link.href = url
                                    link.setAttribute('download', att.fileName || 'attachment')
                                    document.body.appendChild(link)
                                    link.click()
                                    link.remove()
                                    window.URL.revokeObjectURL(url)
                                  } catch (error) {
                                    console.error('Download failed:', error)
                                    toast.error('Failed to download attachment')
                                  }
                                }}
                                className="text-primary-600 hover:text-primary-800 flex items-center gap-1"
                              >
                                <Download className="w-4 h-4" /> Download
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-gray-500 text-sm">No attachments found</p>
                )}
              </div>

              {/* Notes from Zoho */}
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-4">Notes</h2>
                {Array.isArray(zohoDetails.notes) && zohoDetails.notes.length > 0 ? (
                  <div className="space-y-3">
                    {zohoDetails.notes.map((note, i) => (
                      <div key={i} className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                        {note.title && <p className="font-medium text-gray-900 dark:text-gray-100">{note.title}</p>}
                        <p className="text-gray-700 dark:text-gray-300">{note.text}</p>
                        <p className="text-xs text-gray-500 mt-2">
                          {note.createdAt && format(new Date(note.createdAt), 'MMM dd, yyyy h:mm a')}
                          {note.createdByName && ` • by ${note.createdByName}`}
                        </p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-sm">No notes found</p>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Timeline Tab */}
      {activeTab === 'timeline' && (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-4 flex items-center gap-2">
            <Clock className="w-5 h-5" /> Timeline History
          </h2>
          
          {loadingZoho ? (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
            </div>
          ) : (
            <>
              {/* Zoho Timeline */}
              {Array.isArray(zohoDetails?.timeline) && zohoDetails.timeline.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-sm font-medium text-green-600 dark:text-green-400 mb-4">From Zoho CRM</h3>
                  <div className="relative">
                    {/* Timeline line */}
                    <div className="absolute left-5 top-0 bottom-0 w-0.5 bg-gray-200 dark:bg-gray-700"></div>
                    
                    <div className="space-y-6">
                      {zohoDetails.timeline.map((item, index) => {
                        // Get icon based on type
                        const getIcon = () => {
                          switch (item.type) {
                            case 'Email': return <Mail className="w-4 h-4 text-blue-600" />;
                            case 'Note': return <FileText className="w-4 h-4 text-yellow-600" />;
                            case 'Attachment': return <Paperclip className="w-4 h-4 text-purple-600" />;
                            case 'Campaign': return <TrendingUp className="w-4 h-4 text-pink-600" />;
                            case 'Lead': return <User className="w-4 h-4 text-green-600" />;
                            case 'Update': return <Edit className="w-4 h-4 text-orange-600" />;
                            default: return <Clock className="w-4 h-4 text-gray-600" />;
                          }
                        };
                        
                        // Get background color based on type
                        const getBgColor = () => {
                          switch (item.type) {
                            case 'Email': return 'bg-blue-100 dark:bg-blue-900';
                            case 'Note': return 'bg-yellow-100 dark:bg-yellow-900';
                            case 'Attachment': return 'bg-purple-100 dark:bg-purple-900';
                            case 'Campaign': return 'bg-pink-100 dark:bg-pink-900';
                            case 'Lead': return 'bg-green-100 dark:bg-green-900';
                            case 'Update': return 'bg-orange-100 dark:bg-orange-900';
                            default: return 'bg-gray-100 dark:bg-gray-700';
                          }
                        };

                        return (
                          <div key={`zoho-${index}`} className="relative flex gap-4 pl-2">
                            {/* Icon */}
                            <div className={`flex-shrink-0 w-8 h-8 ${getBgColor()} rounded-full flex items-center justify-center z-10 border-2 border-white dark:border-gray-800`}>
                              {getIcon()}
                            </div>
                            
                            {/* Content */}
                            <div className="flex-1 pb-4">
                              <div className="flex items-start justify-between">
                                <div>
                                  <p className="font-medium text-gray-900 dark:text-gray-100">{item.title}</p>
                                  {item.description && (
                                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{item.description}</p>
                                  )}
                                  {item.relatedRecord?.name && item.type !== 'Note' && (
                                    <p className="text-sm text-blue-600 dark:text-blue-400 mt-1 flex items-center gap-1">
                                      <ExternalLink className="w-3 h-3" />
                                      {item.relatedRecord.name}
                                    </p>
                                  )}
                                </div>
                                {item.date && (
                                  <span className="text-xs text-gray-500 whitespace-nowrap ml-2">
                                    {format(new Date(item.date), 'hh:mm a')}
                                  </span>
                                )}
                              </div>
                              <p className="text-xs text-gray-500 mt-1">
                                by {item.createdBy || 'System'}
                                {item.date && ` • ${format(new Date(item.date), 'MMM dd, yyyy')}`}
                              </p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}

              {/* Local Interactions */}
              {lead.interactions?.length > 0 && (
                <div className="space-y-4">
                  <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400">Local Interactions</h3>
                  {lead.interactions.map((item, index) => (
                    <div key={`local-${index}`} className="flex gap-4 pb-4 border-b border-gray-100 dark:border-gray-700 last:border-0">
                      <div className="flex-shrink-0 w-10 h-10 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center">
                        {item.type === 'Email' ? <Mail className="w-5 h-5 text-blue-600" /> :
                         item.type === 'Call' ? <Phone className="w-5 h-5 text-green-600" /> :
                         item.type === 'Meeting' ? <Calendar className="w-5 h-5 text-purple-600" /> :
                         <Clock className="w-5 h-5 text-gray-600" />}
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-gray-900 dark:text-gray-100">{item.type}</p>
                        {item.notes && <p className="text-sm text-gray-600 dark:text-gray-400">{item.notes}</p>}
                        {item.outcome && <p className="text-sm text-gray-500">Outcome: {item.outcome}</p>}
                        <p className="text-xs text-gray-500 mt-1">
                          {item.date && format(new Date(item.date), 'MMM dd, yyyy HH:mm')}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Empty state */}
              {(!Array.isArray(zohoDetails?.timeline) || zohoDetails.timeline.length === 0) && !lead.interactions?.length && (
                <p className="text-center py-8 text-gray-500">No timeline activities yet.</p>
              )}
            </>
          )}
        </div>
      )}

      {/* Notes Tab */}
      {activeTab === 'notes' && (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-4 flex items-center gap-2">
            <MessageSquare className="w-5 h-5" /> Notes & Comments
            {lead.crmId && <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded">Syncs to Zoho</span>}
          </h2>
          
          {/* Add Note */}
          <div className="mb-6">
            <textarea
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              placeholder="Add a note... (will sync to Zoho CRM if connected)"
              className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg resize-none bg-white dark:bg-gray-700"
              rows={3}
            />
            <button
              onClick={handleAddComment}
              disabled={!newComment.trim()}
              className="mt-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:bg-gray-300"
            >
              Add Note {lead.crmId && '& Sync to Zoho'}
            </button>
          </div>

          {loadingZoho ? (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
            </div>
          ) : (
            <>
              {/* Zoho Notes */}
              {Array.isArray(zohoDetails?.notes) && zohoDetails.notes.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-sm font-medium text-green-600 dark:text-green-400 mb-3">From Zoho CRM ({zohoDetails.notes.length})</h3>
                  <div className="space-y-3">
                    {zohoDetails.notes.map((note, index) => (
                      <div key={`zoho-${index}`} className="border-l-4 border-green-500 bg-green-50 dark:bg-green-900/20 p-4 rounded-r-lg">
                        <p className="text-gray-900 dark:text-gray-100">{note.text || note.title}</p>
                        <p className="text-xs text-gray-500 mt-2">
                          {note.createdAt && format(new Date(note.createdAt), 'MMM dd, yyyy HH:mm')}
                          {note.createdByName && ` • by ${note.createdByName}`}
                          <span className="ml-2 text-green-600">• Zoho</span>
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Local Notes */}
              {lead.notes?.length > 0 && (
                <div className="space-y-3">
                  <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-3">Local Notes ({lead.notes.length})</h3>
                  {[...lead.notes].reverse().map((note, index) => (
                    <div key={`local-${index}`} className="border-l-4 border-primary-500 bg-gray-50 dark:bg-gray-700 p-4 rounded-r-lg">
                      <p className="text-gray-900 dark:text-gray-100">{note.text}</p>
                      <p className="text-xs text-gray-500 mt-2">
                        {format(new Date(note.createdAt), 'MMM dd, yyyy HH:mm')}
                        {note.createdByName && ` • by ${note.createdByName}`}
                      </p>
                    </div>
                  ))}
                </div>
              )}

              {/* Empty state */}
              {(!Array.isArray(zohoDetails?.notes) || zohoDetails.notes.length === 0) && !lead.notes?.length && (
                <p className="text-center py-8 text-gray-500">No notes yet. Add your first note above.</p>
              )}
            </>
          )}
        </div>
      )}
    </div>
  )
}

export default LeadDetails
